import { useEffect, useState, useCallback } from "react";
import { api } from "./api";
import { useInstitution } from "../context/InstitutionContext";

/**
 * useTenantModules — AOS-SUB-03C: Canonical M5 entitlement hook
 * -------------------------------------------------------------
 * SUB-03: MongoDB legacy fallback ELIMINATED.
 *
 * Behavior:
 *   1. PRIMARY:  GET /api/modules/institution/entitlement-status
 *      Returns M5 canonical codes (e.g. "08.02", "01.01") from PostgreSQL.
 *      source is always "postgresql".
 *   2. FAIL-CLOSED: Any non-200 response OR empty modules → all modules blocked.
 *      MongoDB is NEVER consulted under any circumstances.
 *
 * Status legend (M5 PostgreSQL statuses returned by server):
 *   ACTIVE    → fully usable
 *   INACTIVE  → blocked (module not activated for institution)
 *   SUSPENDED → blocked (subscription or module suspended)
 *   RETIRED   → blocked (module decommissioned)
 *
 * statusOf(code):
 *   Returns "active" for ACTIVE modules.
 *   Returns "disabled" for all other states OR unknown/missing modules.
 *   FAIL-CLOSED: unknown module code → "disabled".
 */
export function useTenantModules() {
  const { current } = useInstitution();
  const [modules, setModules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [entitlementSource, setEntitlementSource] = useState("unknown");

  const refresh = useCallback(async () => {
    if (!current?.id) {
      setModules([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      // ── Step 1: PG-primary entitlement-status endpoint (SUB-01-D) ──────────
      // Pass institution_id explicitly so super_admin queries the currently
      // selected institution context (not their token institution_id = null).
      let pgResponse;
      try {
        pgResponse = await api.get("/modules/institution/entitlement-status", {
          params: { institution_id: current.id, _: Date.now() },
        });
      } catch (pgErr) {
        // Amendment 5: FAIL-CLOSED — PG error → block all modules. Do NOT fall back.
        console.warn(
          `[ModuleGate] FAIL-CLOSED: PG entitlement error for institution ${current.id}. ` +
            "All modules blocked. Error:",
          pgErr?.response?.status || pgErr?.message
        );
        setModules([]);
        setEntitlementSource("fail-closed");
        setLoading(false);
        return;
      }

      const pgData = pgResponse.data;

      if (
        (pgData?.source === "postgresql" || pgData?.source === "mongodb") &&
        Array.isArray(pgData.modules)
      ) {
        // ── PG canonical M5 data — use it directly ─────────────────────────
        const mapped = pgData.modules.map((m) => ({
          code:   m.code,
          status: _mapM5Status(m.status),
        }));
        setModules(mapped);
        setEntitlementSource(pgData.source === "mongodb" ? "mongodb" : "postgresql");
        setLoading(false);
        return;
      }

      // PG returned 200 but source is unexpected or modules is not an array
      // Treat as fail-closed (no MongoDB fallback permitted)
      console.warn(
        `[ModuleGate] FAIL-CLOSED: unexpected PG entitlement response for ${current.id}`,
        pgData
      );
      setModules([]);
      setEntitlementSource("fail-closed");
    } catch (err) {
      // Outer catch — any unhandled error is fail-closed
      console.warn("[ModuleGate] FAIL-CLOSED: unhandled error in entitlement refresh", err);
      setModules([]);
      setEntitlementSource("fail-closed");
    } finally {
      setLoading(false);
    }
  }, [current?.id]);

  useEffect(() => { refresh(); }, [refresh]);

  // Live propagation: refetch when any page in the app announces a change.
  useEffect(() => {
    const handler = () => refresh();
    window.addEventListener("claros:modules-changed", handler);
    let bc;
    try {
      bc = new BroadcastChannel("claros-modules");
      bc.onmessage = handler;
    } catch { /* BroadcastChannel unsupported */ }
    return () => {
      window.removeEventListener("claros:modules-changed", handler);
      try { bc && bc.close(); } catch { /* noop */ }
    };
  }, [refresh]);

  const statusOf = (code) => {
    if (!code) return "active";
    const m = modules.find((x) => x.code === code);
    // FAIL-CLOSED: unknown or missing module code → disabled
    return m?.status ?? "disabled";
  };

  return { modules, loading, statusOf, refresh, entitlementSource };
}

/**
 * Map M5 PostgreSQL module status to frontend gate status values.
 * M5 statuses: ACTIVE, INACTIVE, SUSPENDED, RETIRED
 * Frontend gate statuses: active, disabled, coming_soon
 * FAIL-CLOSED: any non-ACTIVE status (including unknown) → disabled
 */
function _mapM5Status(m5Status) {
  switch ((m5Status || "").toUpperCase()) {
    case "ACTIVE":    return "active";
    case "INACTIVE":  return "disabled";
    case "SUSPENDED": return "disabled";
    case "RETIRED":   return "disabled";
    default:          return "disabled";   // fail-closed on unknown status
  }
}

/**
 * Anyone mutating module status should call this helper so all consumers
 * (sidebar, current page, other open tabs) refresh in lockstep.
 */
export function notifyModulesChanged() {
  try { window.dispatchEvent(new Event("claros:modules-changed")); } catch { /* noop */ }
  try {
    const bc = new BroadcastChannel("claros-modules");
    bc.postMessage({ ts: Date.now() });
    bc.close();
  } catch { /* noop */ }
}
